/**
 * Video Gallery JavaScript
 * Handles filtering, sorting, pagination, and modal functionality
 */

class VideoGallery {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.videosPerPage = parseInt(container.dataset.videosPerPage) || 6;
    console.log('VideoGallery initialized with:', {
      videosPerPage: this.videosPerPage,
      totalItems: container.querySelectorAll('.video-gallery__item').length,
      dataAttribute: container.dataset.videosPerPage
    });
    
    // Elements
    this.tabs = container.querySelectorAll('.video-gallery__tab');
    this.sortSelect = container.querySelector('.video-gallery__sort-select');
    this.grid = container.querySelector('.video-gallery__grid');
    this.items = Array.from(container.querySelectorAll('.video-gallery__item'));
    this.paginationContainers = container.querySelectorAll('.video-gallery__pagination-numbers');
    this.prevButtons = container.querySelectorAll('.video-gallery__pagination-prev');
    this.nextButtons = container.querySelectorAll('.video-gallery__pagination-next');
    this.searchInput = container.querySelector('.video-gallery__search-input');
    this.heroPlayer = container.querySelector('.video-gallery__hero-player');
    this.heroPlaceholder = container.querySelector('.video-gallery__hero-placeholder');
    this.heroIframe = container.querySelector('.video-gallery__hero-iframe');
    this.heroTitle = container.querySelector('.video-gallery__hero-title');
    
    // State
    this.currentCategory = 'all';
    this.currentSort = 'newest';
    this.currentPage = 1;
    this.currentQuery = '';
    this.filteredItems = [...this.items];
    this.sortedItems = [...this.items];
    
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.setupPagination();
    this.updateDisplay();
    this.setupYouTubeThumbnails();
    this.setupYouTubeTitles();
  }
  
  bindEvents() {
    // Tab clicks
    this.tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleTabClick(tab);
      });
    });
    
    // Dropdown functionality
    const dropdowns = this.container.querySelectorAll('.video-gallery__dropdown');
    dropdowns.forEach(dropdown => {
      const trigger = dropdown.querySelector('.video-gallery__dropdown-trigger');
      const content = dropdown.querySelector('.video-gallery__dropdown-content');
      
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Close other dropdowns
        dropdowns.forEach(otherDropdown => {
          if (otherDropdown !== dropdown) {
            otherDropdown.classList.remove('active');
          }
        });
        
        // Toggle current dropdown
        dropdown.classList.toggle('active');
      });
      
      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!dropdown.contains(e.target)) {
          dropdown.classList.remove('active');
        }
      });
    });
    
    // Sort change
    console.log('Sort select element:', this.sortSelect);
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', (e) => {
        console.log('Sort dropdown changed to:', e.target.value);
        this.handleSortChange(e.target.value);
      });
    } else {
      console.error('Sort select element not found!');
    }
    
    // Search input
    if (this.searchInput) {
      const debounced = this.debounce((value) => {
        this.handleSearchChange(value);
      }, 200);
      this.searchInput.addEventListener('input', (e) => {
        debounced(e.target.value);
      });
    }
    
    // Pagination - bind to all prev/next buttons
    this.prevButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.goToPage(this.currentPage - 1);
      });
    });
    
    this.nextButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.goToPage(this.currentPage + 1);
      });
    });
    
    // Video item clicks
    this.items.forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleVideoClick(item);
      });
    });
    
    // Hero player click
    if (this.heroPlaceholder) {
      this.heroPlaceholder.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleHeroClick();
      });
    }
  }
  
  handleTabClick(tab) {
    // Don't handle clicks on dropdown triggers (they're handled separately)
    if (tab.classList.contains('video-gallery__dropdown-trigger')) {
      return;
    }
    
    // Update active tab
    this.tabs.forEach(t => t.classList.remove('active'));
    tab.classList.add('active');
    
    // Close all dropdowns
    const dropdowns = this.container.querySelectorAll('.video-gallery__dropdown');
    dropdowns.forEach(dropdown => {
      dropdown.classList.remove('active');
    });
    
    // Update category
    this.currentCategory = tab.dataset.category;
    this.currentPage = 1;
    
    this.filterAndSort();
  }
  
  handleSortChange(sortType) {
    console.log('Sort changed to:', sortType);
    this.currentSort = sortType;
    this.currentPage = 1;
    
    this.filterAndSort();
  }

  handleSearchChange(query) {
    this.currentQuery = (query || '').toLocaleLowerCase().trim();
    this.currentPage = 1;
    this.filterAndSort();
  }
  
  handleVideoClick(item) {
    const videoUrl = this.getVideoUrl(item);
    const videoTitle = item.querySelector('.video-gallery__title').textContent;
    
    console.log('Video item clicked:', {
      videoUrl: videoUrl,
      videoTitle: videoTitle,
      itemDataset: item.dataset
    });
    
    if (videoUrl) {
      this.playInHero(videoUrl, videoTitle);
    }
  }
  
  handleHeroClick() {
    const videoUrl = this.heroPlayer.dataset.videoUrl;
    if (videoUrl) {
      console.log('Hero player clicked, video URL:', videoUrl);
      this.playInHero(videoUrl);
    }
  }
  
  getVideoUrl(item) {
    // Get video URL directly from the item's data attribute
    const videoUrl = item.dataset.videoUrl;
    
    if (videoUrl) {
      return videoUrl; // Return the raw URL, not the embed URL
    }
    
    return null;
  }
  
  getBlockData(blockId) {
    // This would typically come from the theme's section data
    // For now, we'll extract from the DOM
    const item = this.container.querySelector(`[data-block-id="${blockId}"]`);
    if (item) {
      return {
        video_url: item.dataset.videoUrl || '',
        title: item.dataset.title || '',
        category: item.dataset.category || '',
        date: item.dataset.date || '',
        popularity: parseInt(item.dataset.popularity) || 0
      };
    }
    return null;
  }
  
  extractYouTubeVideoId(url) {
    if (!url) return null;
    
    // Handle different YouTube URL formats
    let videoId = '';
    
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1];
    }
    
    // Remove any additional parameters
    if (videoId.includes('&')) {
      videoId = videoId.split('&')[0];
    }
    
    return videoId;
  }
  
  extractYouTubeEmbedUrl(url) {
    const videoId = this.extractYouTubeVideoId(url);
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?rel=0&showinfo=0&vq=720`;
    }
    
    return null;
  }
  
  getYouTubeThumbnailUrl(videoId, quality = 'maxresdefault') {
    if (!videoId) return null;
    
    // Try different thumbnail qualities in order of preference
    const qualities = ['maxresdefault', 'hqdefault', 'mqdefault', 'sddefault'];
    const preferredQuality = qualities.includes(quality) ? quality : 'maxresdefault';
    
    return `https://img.youtube.com/vi/${videoId}/${preferredQuality}.jpg`;
  }
  
  playInHero(videoUrl, title = null) {
    console.log('playInHero called with:', { videoUrl, title });
    
    if (!this.heroPlayer || !this.heroIframe) {
      console.error('Hero player elements not found:', {
        heroPlayer: this.heroPlayer,
        heroIframe: this.heroIframe
      });
      return;
    }
    
    // Extract YouTube video ID and create embed URL
    const videoId = this.extractYouTubeVideoId(videoUrl);
    console.log('Extracted video ID:', videoId);
    
    if (!videoId) {
      console.error('Could not extract video ID from URL:', videoUrl);
      return;
    }
    
    // Try a simpler embed URL first
    const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    
    // Create iframe
    const iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.frameBorder = '0';
    iframe.allowFullscreen = true;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.title = 'YouTube video player';
    iframe.setAttribute('loading', 'lazy');
    
    // Clear previous content and add new iframe
    this.heroIframe.innerHTML = '';
    this.heroIframe.appendChild(iframe);
    
    // Test if iframe is actually in the DOM
    console.log('Iframe added to DOM:', {
      iframeInDOM: this.heroIframe.contains(iframe),
      iframeParent: iframe.parentElement,
      iframeSrc: iframe.src,
      heroIframeChildren: this.heroIframe.children.length
    });
    
    // Hide placeholder and show iframe
    if (this.heroPlaceholder) {
      this.heroPlaceholder.style.display = 'none';
    }
    
    // Show iframe using CSS class
    this.heroIframe.classList.add('show');
    this.heroIframe.style.display = 'block';
    
    // Force iframe to be visible
    iframe.style.display = 'block';
    iframe.style.visibility = 'visible';
    iframe.style.opacity = '1';
    
    console.log('Iframe created and should be visible:', {
      iframeSrc: iframe.src,
      iframeDisplay: iframe.style.display,
      heroIframeDisplay: this.heroIframe.style.display,
      heroPlaceholderDisplay: this.heroPlaceholder ? this.heroPlaceholder.style.display : 'N/A'
    });
    
    // Add a small delay to ensure iframe loads properly
    setTimeout(() => {
      if (iframe.style.display !== 'block') {
        iframe.style.display = 'block';
        console.log('Forced iframe display to block after delay');
      }
      
      // Check if iframe is actually visible after delay
      const iframeRect = iframe.getBoundingClientRect();
      console.log('Iframe visibility check:', {
        width: iframeRect.width,
        height: iframeRect.height,
        top: iframeRect.top,
        left: iframeRect.left,
        display: iframe.style.display,
        visibility: iframe.style.visibility
      });
    }, 100);
    
    // Update title if provided
    if (title && this.heroTitle) {
      this.heroTitle.textContent = title;
      console.log('Updated hero title to:', title);
    } else if (this.heroTitle) {
      // If no title provided, try to fetch from YouTube
      const videoId = this.extractYouTubeVideoId(videoUrl);
      if (videoId) {
        console.log('Fetching YouTube title for hero player video:', videoId);
        this.loadYouTubeTitle(this.heroTitle, videoId);
      }
    }
    
    console.log('Playing video in hero:', embedUrl);
  }
  
  filterAndSort() {
    // Ensure items is an array and has items
    if (!Array.isArray(this.items) || this.items.length === 0) {
      this.filteredItems = [];
      this.sortedItems = [];
      this.updateDisplay();
      return;
    }
    
    // Filter by category and search query
    this.filteredItems = this.items.filter(item => {
      const inCategory = this.currentCategory === 'all' ? true : item.dataset.category === this.currentCategory;
      if (!inCategory) return false;
      if (!this.currentQuery) return true;
      const titleElement = item.querySelector('.video-gallery__title');
      const titleText = titleElement ? titleElement.textContent : (item.dataset.title || '');
      const descText = item.dataset.description || '';
      const haystack = `${titleText}\n${descText}`.toLocaleLowerCase();
      return haystack.includes(this.currentQuery);
    });
    
    // Sort items
    console.log('Sorting items with method:', this.currentSort);
    console.log('Filtered items before sorting:', this.filteredItems.length);
    
    this.sortedItems = [...this.filteredItems].sort((a, b) => {
      let result = 0;
      
      // Resolve titles from DOM, fallback to data attributes
      const titleAElement = a.querySelector('.video-gallery__title');
      const titleBElement = b.querySelector('.video-gallery__title');
      const rawTitleA = titleAElement ? titleAElement.textContent.trim() : (a.dataset.title || '');
      const rawTitleB = titleBElement ? titleBElement.textContent.trim() : (b.dataset.title || '');
      const titleA = rawTitleA || '';
      const titleB = rawTitleB || '';
      const normalizedA = titleA.toLocaleLowerCase();
      const normalizedB = titleB.toLocaleLowerCase();
      
      switch (this.currentSort) {
        case 'newest':
          result = this.compareDates(b.dataset.date, a.dataset.date);
          break;
        case 'oldest':
          result = this.compareDates(a.dataset.date, b.dataset.date);
          break;
        case 'alphabetical-az':
          result = normalizedA.localeCompare(normalizedB, undefined, { sensitivity: 'base' });
          break;
        case 'alphabetical-za':
          result = normalizedB.localeCompare(normalizedA, undefined, { sensitivity: 'base' });
          break;
        case 'popular': {
          const popA = parseInt(a.dataset.popularity, 10);
          const popB = parseInt(b.dataset.popularity, 10);
          result = (isNaN(popB) ? 0 : popB) - (isNaN(popA) ? 0 : popA);
          break;
        }
        case 'least-popular': {
          const popA = parseInt(a.dataset.popularity, 10);
          const popB = parseInt(b.dataset.popularity, 10);
          result = (isNaN(popA) ? 0 : popA) - (isNaN(popB) ? 0 : popB);
          break;
        }
        default:
          result = 0;
      }
      
      console.log(`Comparing "${titleA}" vs "${titleB}" (${this.currentSort}): ${result}`);
      return result;
    });
    
    console.log('Sorted items after sorting:', this.sortedItems.length);
    
    this.updateDisplay();
  }

  debounce(fn, delay = 200) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), delay);
    };
  }
  
  compareDates(dateA, dateB) {
    if (!dateA && !dateB) return 0;
    if (!dateA) return -1;
    if (!dateB) return 1;
    // Accept YYYYMMDD or YYYY-MM-DD
    const normalize = (d) => String(d).replace(/[^0-9]/g, '').slice(0, 8);
    const aNum = parseInt(normalize(dateA), 10);
    const bNum = parseInt(normalize(dateB), 10);
    const a = isNaN(aNum) ? 0 : aNum;
    const b = isNaN(bNum) ? 0 : bNum;
    return a - b;
  }
  
  updateDisplay() {
    // Ensure we have valid arrays
    if (!Array.isArray(this.items) || !Array.isArray(this.sortedItems)) {
      return;
    }
    
    const totalPages = Math.ceil(this.sortedItems.length / this.videosPerPage);
    const startIndex = (this.currentPage - 1) * this.videosPerPage;
    const endIndex = startIndex + this.videosPerPage;
    const currentItems = this.sortedItems.slice(startIndex, endIndex);
    
    console.log('Pagination debug:', {
      totalItems: this.sortedItems.length,
      videosPerPage: this.videosPerPage,
      currentPage: this.currentPage,
      totalPages: totalPages,
      startIndex: startIndex,
      endIndex: endIndex,
      currentItemsCount: currentItems.length
    });
    
    // Hide all items
    this.items.forEach(item => {
      if (item && item.classList) {
        item.classList.add('hidden');
      }
    });
    
    // Clear grid and re-add items in sorted order
    if (this.grid) {
      // Remove all items temporarily
      const allItems = Array.from(this.grid.children);
      allItems.forEach(item => item.remove());
      
      // Add items back in sorted order
      currentItems.forEach(item => {
        if (item && item.classList) {
          item.classList.remove('hidden');
          this.grid.appendChild(item);
        }
      });
      
      // Hide items that shouldn't be visible
      this.items.forEach(item => {
        if (!currentItems.includes(item)) {
          item.classList.add('hidden');
        }
      });

      // Ensure titles for newly visible items are loaded
      // Only fetch for elements that still show the loading placeholder
      // Debounce to avoid multiple rapid calls during sorting/pagination
      clearTimeout(this._titlesSetupTimeout);
      this._titlesSetupTimeout = setTimeout(() => {
        this.setupYouTubeTitles();
      }, 50);
    }
    
    // Update pagination
    this.updatePagination(totalPages);
    
    // Show empty state if no items
    if (this.sortedItems.length === 0) {
      this.showEmptyState();
    } else {
      this.hideEmptyState();
    }
  }
  
  updatePagination(totalPages) {
    // Update all pagination containers
    this.paginationContainers.forEach(container => {
      if (!container) return;
      
      // Clear existing pagination
      container.innerHTML = '';
      
      // Create pagination numbers
      for (let i = 1; i <= totalPages; i++) {
        const pageButton = document.createElement('button');
        pageButton.className = 'video-gallery__pagination-number';
        pageButton.textContent = i;
        
        if (i === this.currentPage) {
          pageButton.classList.add('active');
        }
        
        pageButton.addEventListener('click', () => {
          this.goToPage(i);
        });
        
        container.appendChild(pageButton);
      }
    });
    
    // Update all prev buttons
    this.prevButtons.forEach(button => {
      button.disabled = this.currentPage <= 1;
    });
    
    // Update all next buttons
    this.nextButtons.forEach(button => {
      button.disabled = this.currentPage >= totalPages;
    });
  }
  
  goToPage(page) {
    const totalPages = Math.ceil(this.sortedItems.length / this.videosPerPage);
    
    if (page >= 1 && page <= totalPages) {
      this.currentPage = page;
      this.updateDisplay();
      
      // Scroll to top of gallery
      this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
  
  showEmptyState() {
    let emptyState = this.container.querySelector('.video-gallery__empty');
    
    if (!emptyState) {
      emptyState = document.createElement('div');
      emptyState.className = 'video-gallery__empty';
      emptyState.innerHTML = `
        <h3>No videos found</h3>
        <p>Try selecting a different category or sorting option.</p>
      `;
      this.grid.appendChild(emptyState);
    }
    
    emptyState.style.display = 'block';
  }
  
  hideEmptyState() {
    const emptyState = this.container.querySelector('.video-gallery__empty');
    if (emptyState) {
      emptyState.style.display = 'none';
    }
  }
  
  setupPagination() {
    // This will be called after the initial setup
    this.filterAndSort();
  }
  
  setupYouTubeThumbnails() {
    // Find all video items that don't have custom images
    const videoItems = this.container.querySelectorAll('.video-gallery__item');
    
    videoItems.forEach(item => {
      const videoUrl = item.dataset.videoUrl;
      const thumbnail = item.querySelector('.video-gallery__image');
      const placeholder = item.querySelector('.video-gallery__placeholder');
      
      if (videoUrl && thumbnail && !thumbnail.src.includes('cdn.shopify.com')) {
        // This is a YouTube thumbnail, let's ensure it loads properly
        const videoId = this.extractYouTubeVideoId(videoUrl);
        
        if (videoId) {
          // Try different thumbnail qualities
          this.loadYouTubeThumbnail(thumbnail, videoId, placeholder);
        }
      }
    });
  }
  
  setupYouTubeTitles() {
    // Find all video titles that need YouTube titles
    const titleElements = this.container.querySelectorAll('.video-gallery__title');
    const heroTitle = this.container.querySelector('.video-gallery__hero-title');
    
    console.log('Found title elements:', titleElements.length);
    console.log('Hero title element:', heroTitle);
    
    // Handle hero title first (only if not already loaded)
    if (heroTitle) {
      const heroAlreadyLoaded = heroTitle.dataset.titleLoaded === '1' || (!heroTitle.querySelector('.video-gallery__title-loading') && heroTitle.textContent.trim() !== '');
      if (!heroAlreadyLoaded) {
        const heroVideoUrl = this.heroPlayer ? this.heroPlayer.dataset.videoUrl : null;
        if (heroVideoUrl) {
          const videoId = this.extractYouTubeVideoId(heroVideoUrl);
          if (videoId) {
            console.log('Loading YouTube title for hero player with video ID:', videoId);
            this.loadYouTubeTitle(heroTitle, videoId);
          }
        }
      } else {
        console.log('Skipping hero title - already loaded');
      }
    }
    
    titleElements.forEach((titleElement, index) => {
      const videoId = titleElement.dataset.videoId;
      const customTitle = titleElement.dataset.customTitle;
      const alreadyLoaded = titleElement.dataset.titleLoaded === '1';
      const hasLoadingText = !!titleElement.querySelector('.video-gallery__title-loading');
      const currentText = titleElement.textContent.trim();
      const shouldFetch = !!videoId && (!customTitle || customTitle.trim() === '') && (!alreadyLoaded || hasLoadingText || currentText === '' || currentText === 'Video Title');

      console.log(`Title element ${index}:`, {
        videoId,
        customTitle,
        alreadyLoaded,
        hasLoadingText,
        currentText,
        shouldFetch
      });

      if (shouldFetch) {
        console.log(`Loading YouTube title for element ${index} with video ID:`, videoId);
        this.loadYouTubeTitle(titleElement, videoId);
      } else {
        console.log(`Skipping element ${index} - reason: ${!videoId ? 'no video ID' : alreadyLoaded ? 'already loaded' : 'has custom title'}`);
      }
    });
  }
  
  async loadYouTubeTitle(titleElement, videoId) {
    try {
      console.log('Fetching YouTube title for video ID:', videoId);
      
      // Use YouTube oEmbed API to get video title
      const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
      
      if (response.ok) {
        const data = await response.json();
        const title = data.title;
        
        console.log('YouTube API response:', data);
        
        if (title) {
          // Remove loading text and set the YouTube title
          const loadingElement = titleElement.querySelector('.video-gallery__title-loading');
          if (loadingElement) {
            loadingElement.remove();
          }
          titleElement.textContent = title;
          titleElement.dataset.titleLoaded = '1';
          console.log('Set YouTube title:', title);
          // Debounce a re-sort after titles are populated so A/Z sorts use the new titles
          clearTimeout(this._resortAfterTitleTimeout);
          this._resortAfterTitleTimeout = setTimeout(() => {
            // Only resort if using a title-based sort
            if (this.currentSort === 'alphabetical-az' || this.currentSort === 'alphabetical-za') {
              // Guard against infinite resort loop: only trigger if DOM order is out of sync
              const firstVisible = this.sortedItems[0];
              const firstInDom = this.grid ? this.grid.querySelector('.video-gallery__item') : null;
              if (firstVisible && firstInDom && firstVisible !== firstInDom) {
                this.filterAndSort();
              }
            }
          }, 200);
        } else {
          console.warn('No title found in YouTube API response');
          this.setFallbackTitle(titleElement);
        }
      } else {
        console.warn('YouTube API response not ok:', response.status, response.statusText);
        this.setFallbackTitle(titleElement);
      }
    } catch (error) {
      console.warn('Failed to fetch YouTube title:', error);
      this.setFallbackTitle(titleElement);
    }
  }
  
  setFallbackTitle(titleElement) {
    const loadingElement = titleElement.querySelector('.video-gallery__title-loading');
    if (loadingElement) {
      // Check if this is the hero title and provide a better fallback
      if (titleElement.classList.contains('video-gallery__hero-title')) {
        loadingElement.textContent = 'Featured Video';
      } else {
        loadingElement.textContent = 'Video Title';
      }
      loadingElement.classList.remove('video-gallery__title-loading');
    }
    // Do NOT mark as loaded so we can retry fetching on next update
  }
  
  loadYouTubeThumbnail(imgElement, videoId, placeholderElement) {
    const qualities = ['maxresdefault', 'hqdefault', 'mqdefault', 'sddefault'];
    let currentQualityIndex = 0;
    
    const tryNextQuality = () => {
      if (currentQualityIndex >= qualities.length) {
        // All qualities failed, show placeholder
        if (imgElement) imgElement.style.display = 'none';
        if (placeholderElement) placeholderElement.style.display = 'flex';
        return;
      }
      
      const quality = qualities[currentQualityIndex];
      const thumbnailUrl = this.getYouTubeThumbnailUrl(videoId, quality);
      
      if (thumbnailUrl) {
        const testImg = new Image();
        testImg.onload = () => {
          // Thumbnail exists, use it
          imgElement.src = thumbnailUrl;
          imgElement.style.display = 'block';
          if (placeholderElement) placeholderElement.style.display = 'none';
        };
        testImg.onerror = () => {
          // This quality doesn't exist, try next
          currentQualityIndex++;
          tryNextQuality();
        };
        testImg.src = thumbnailUrl;
      } else {
        currentQualityIndex++;
        tryNextQuality();
      }
    };
    
    tryNextQuality();
  }
}

// Initialize video galleries when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const videoGalleries = document.querySelectorAll('[data-section-type="video-gallery"]');
  videoGalleries.forEach(container => {
    new VideoGallery(container);
  });
});

// Handle Shopify section events
document.addEventListener('shopify:section:load', function(event) {
  const container = event.target.querySelector('[data-section-type="video-gallery"]');
  if (container) {
    new VideoGallery(container);
  }
});

// Handle Shopify section events for existing sections
document.addEventListener('shopify:section:reload', function(event) {
  const container = event.target.querySelector('[data-section-type="video-gallery"]');
  if (container) {
    new VideoGallery(container);
  }
});

// Export for potential use in theme.js
window.VideoGallery = VideoGallery;
